import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Article } from "@shared/schema";
import { Search as SearchIcon, Loader2, Newspaper, ShoppingBag, Store, Video, Megaphone, Package, MapPin, Bookmark, BookOpen, MessageCircle } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { useRegion } from "@/contexts/RegionContext";
import { useLocation as useLocationContext } from "@/contexts/LocationContext";
import { calculateDistance, formatDistance } from "@/lib/geolocation";
import { LocationBadge } from "@/components/LocationBadge";
import { useSavedSearch } from "@/hooks/useSavedSearch";
import { useTopicLocale } from "@/hooks/use-topic-locale";
import { useToast } from "@/hooks/use-toast";
import { australianStates } from "@shared/suburbs";
import {
  getTopicDescription,
  getTopicLabel,
  getTopicSecondaryLabel,
  searchTopics,
} from "@shared/topic-taxonomy";
import { getTopicProblem } from "@shared/topic-problems";
import {
  getCommunityContentIntent,
  getCommunityContentIntentLabel,
} from "@shared/community-content-intent";
import { extractSearchItems, matchesSearchText } from "@/lib/search-content";
import { HelpRequestButton } from "@/components/HelpRequestButton";

interface MarketListing {
  id: string;
  title: string;
  description: string;
  priceCents: number;
  images: string[];
  state: string;
  suburb?: string;
  createdAt: string;
}

interface MarketplaceItem {
  id: string;
  businessName: string;
  title: string;
  description: string;
  salePrice: string;
  imageUrl: string;
  category: string;
  createdAt: string;
}

interface Business {
  id: string;
  businessName: string;
  businessNameKo?: string | null;
  businessNameEn?: string | null;
  category?: string | null;
  description?: string | null;
  descriptionKo?: string | null;
  address?: string | null;
  contactPhone?: string | null;
  state?: string | null;
  suburb?: string | null;
  postcode?: string | null;
  membershipTier?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

interface VideoContent {
  id: string;
  title: string;
  description?: string;
  youtubeId: string;
  thumbnailUrl?: string;
  category: string;
  region: string;
  createdAt: string;
}

interface CityClassified {
  id: string;
  title: string;
  description: string;
  category: string;
  state: string;
  suburb?: string;
  contactPhone?: string;
  images?: string[];
  createdAt: string;
}

interface CommunityPost {
  id: string | number;
  title: string;
  content?: string | null;
  category?: string | null;
  suburb?: string | null;
  state?: string | null;
  createdAt?: string | null;
  metadata?: { contentIntent?: unknown } | null;
  location?: Record<string, unknown> | string | null;
}

const searchCategoryToTab: Record<string, string> = {
  topics: "topics",
  news: "articles",
  community: "market",
  "used-goods": "market",
  "local-business": "businesses",
  "community-talk": "community",
  video: "videos",
};

export default function Search() {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [sortBy, setSortBy] = useState<"latest" | "distance" | "popular">("latest");
  const { selectedRegion, selectedSuburb, selectedRadius } = useRegion();
  const { userLocation } = useLocationContext();
  const { locale, isKorean } = useTopicLocale();
  const { saveSearch, isSearchSaved } = useSavedSearch();
  const { toast } = useToast();

  // Get search query from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlQuery = params.get('q');
    const urlCategory = params.get('category');
    if (urlQuery) {
      setSearchQuery(urlQuery);
      setDebouncedQuery(urlQuery);
    }
    if (urlCategory && searchCategoryToTab[urlCategory]) {
      setActiveTab(searchCategoryToTab[urlCategory]);
    }
  }, []);

  // Fetch all content types
  const { data: articles = [], isLoading: articlesLoading } = useQuery<Article[]>({
    queryKey: ["/api/articles"],
  });

  const { data: marketListings = [], isLoading: marketLoading } = useQuery<MarketListing[]>({
    queryKey: ["/api/market"],
  });

  const { data: marketplaceItems = [], isLoading: marketplaceLoading } = useQuery<MarketplaceItem[]>({
    queryKey: ["/api/marketplace/items"],
  });

  const { data: businesses = [], isLoading: businessesLoading } = useQuery<Business[]>({
    queryKey: ["search-businesses", debouncedQuery, selectedRegion, selectedSuburb],
    enabled: debouncedQuery.trim().length > 0,
    queryFn: async () => {
      const params = new URLSearchParams({ q: debouncedQuery.trim(), limit: "100" });
      if (selectedRegion !== "ALL") params.set("state", selectedRegion);
      const response = await fetch(`/api/business-directory?${params.toString()}`);
      if (!response.ok) throw new Error("Failed to search businesses");
      return extractSearchItems<Business>(await response.json());
    },
  });

  const { data: videos = [], isLoading: videosLoading } = useQuery<VideoContent[]>({
    queryKey: ["/api/videos"],
  });

  const { data: classifieds = [], isLoading: classifiedsLoading } = useQuery<CityClassified[]>({
    queryKey: ["/api/city-classifieds"],
  });

  const { data: communityPosts = [], isLoading: communityLoading } = useQuery<CommunityPost[]>({
    queryKey: ["search-community-posts"],
    queryFn: async () => {
      const response = await fetch("/api/community-talk?limit=100");
      if (!response.ok) throw new Error("Failed to search community posts");
      return extractSearchItems<CommunityPost>(await response.json());
    },
  });

  // Simple debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const isLoading = articlesLoading || marketLoading || marketplaceLoading ||
                    businessesLoading || videosLoading || classifiedsLoading || communityLoading;

  // Filter functions
  const filterArticles = () => {
    if (!debouncedQuery) return [];
    const query = debouncedQuery.toLowerCase();
    return articles.filter((article) => matchesSearchText(
      query, article.title, article.titleKo, article.summary,
      article.summaryKo, article.content, article.contentKo,
    ));
  };

  const filterMarketListings = () => {
    if (!debouncedQuery) return [];
    const query = debouncedQuery.toLowerCase();
    let filtered = marketListings.filter((item) =>
      matchesSearchText(query, item.title, item.description),
    );

    // Phase 5.1: Apply radius-based filtering
    if (userLocation && selectedRadius !== "all") {
      const maxDistance = selectedRadius === "3" ? 3 : selectedRadius === "10" ? 10 : 20;
      filtered = filtered
        .map((item) => {
          // Calculate distance if coordinates are available
          let distance: number | undefined = undefined;
          if (
            (item as any).latitude &&
            (item as any).longitude &&
            typeof (item as any).latitude === "number" &&
            typeof (item as any).longitude === "number"
          ) {
            distance = calculateDistance(
              userLocation.latitude,
              userLocation.longitude,
              (item as any).latitude,
              (item as any).longitude
            );
          }
          return { ...item, distance };
        })
        .filter((item) => {
          // Filter by radius
          if (item.distance !== undefined) {
            return item.distance <= maxDistance;
          }
          // If no coordinates, apply region filter as fallback
          if (selectedRegion !== 'ALL') {
            return item.state === selectedRegion;
          }
          return true;
        });
    } else {
      // Apply location filter (region/suburb) when no user location
      if (selectedRegion !== 'ALL') {
        filtered = filtered.filter((item) => item.state === selectedRegion);
        if (selectedSuburb) {
          filtered = filtered.filter((item) => item.suburb === selectedSuburb);
        }
      }
    }

    // Phase 5.1: Apply sorting
    if (sortBy === "distance" && userLocation) {
      filtered = filtered.sort((a, b) => {
        const distA = (a as any).distance ?? Infinity;
        const distB = (b as any).distance ?? Infinity;
        return distA - distB;
      });
    } else if (sortBy === "latest") {
      filtered = filtered.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } else if (sortBy === "popular") {
      // Sort by favorites/views if available
      filtered = filtered.sort((a, b) => {
        const popularA = ((a as any).favorites || 0) + ((a as any).views || 0);
        const popularB = ((b as any).favorites || 0) + ((b as any).views || 0);
        return popularB - popularA;
      });
    }

    return filtered;
  };

  const filterMarketplaceItems = () => {
    if (!debouncedQuery) return [];
    const query = debouncedQuery.toLowerCase();
    return marketplaceItems.filter((item) =>
      matchesSearchText(query, item.title, item.description, item.businessName),
    );
  };

  const filterBusinesses = () => {
    if (!debouncedQuery) return [];
    const query = debouncedQuery.toLowerCase();
    let filtered = businesses.filter((business) => matchesSearchText(
      query,
      business.businessName,
      business.businessNameKo,
      business.businessNameEn,
      business.description,
      business.descriptionKo,
      business.category,
      business.address,
      business.postcode,
      business.contactPhone,
    ));

    // Apply location filter
    if (selectedRegion !== 'ALL') {
      filtered = filtered.filter((business) => business.state === selectedRegion);
      if (selectedSuburb) {
        // Bulk-imported directory records can have precise coordinates/address
        // before a normalized suburb is available. Keep those discoverable at
        // state level instead of silently hiding them from every local search.
        filtered = filtered.filter(
          (business) => !business.suburb || business.suburb === selectedSuburb,
        );
      }
    }

    // Sort by distance if user location is available
    if (sortBy === "distance" && userLocation) {
      filtered = [...filtered].sort((a, b) => {
        const distance = (business: Business) =>
          typeof business.latitude === "number" && typeof business.longitude === "number"
            ? calculateDistance(userLocation.latitude, userLocation.longitude, business.latitude, business.longitude)
            : Number.POSITIVE_INFINITY;
        return distance(a) - distance(b);
      });
    }

    return filtered;
  };

  const filterVideos = () => {
    if (!debouncedQuery) return [];
    const query = debouncedQuery.toLowerCase();
    return videos.filter((video) => matchesSearchText(query, video.title, video.description));
  };

  const filterClassifieds = () => {
    if (!debouncedQuery) return [];
    const query = debouncedQuery.toLowerCase();
    return classifieds.filter((classified) =>
      matchesSearchText(query, classified.title, classified.description, classified.category),
    );
  };

  const filterCommunityPosts = () => {
    if (!debouncedQuery) return [];
    const query = debouncedQuery.toLowerCase();
    return communityPosts.filter((post) =>
      matchesSearchText(query, post.title, post.content, post.category, post.suburb, post.state),
    );
  };

  const filteredArticles = filterArticles();
  const filteredMarketListings = filterMarketListings();
  const filteredMarketplaceItems = filterMarketplaceItems();
  const filteredBusinesses = filterBusinesses();
  const filteredVideos = filterVideos();
  const filteredClassifieds = filterClassifieds();
  const filteredCommunityPosts = filterCommunityPosts();
  const filteredTopics = useMemo(
    () => (debouncedQuery ? searchTopics(debouncedQuery).slice(0, 6) : []),
    [debouncedQuery],
  );

  const totalResults = filteredTopics.length + filteredArticles.length + filteredMarketListings.length +
                       filteredMarketplaceItems.length + filteredBusinesses.length +
                       filteredVideos.length + filteredClassifieds.length + filteredCommunityPosts.length;

  const formatPrice = (cents: number) => {
    if (cents === 0) return "무료나눔";
    if (cents === -1) return "가격협의";
    return `$${(cents / 100).toLocaleString()}`;
  };

  return (
    <PageContainer size="wide">
        {/* Search Header */}
        <div className="mb-8 space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">통합 검색</h1>
              <p className="text-muted-foreground mt-1">
                {isKorean
                  ? "생활문제 해결 가이드, 사람들의 질문·경험, 뉴스, 동네 정보와 업체를 함께 찾아보세요"
                  : "Search verified life-problem guides, community questions and experiences, news, local posts, and services together"}
              </p>
            </div>
            <LocationBadge />
          </div>
          <div className="relative max-w-2xl">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="search"
              placeholder={
                selectedSuburb
                  ? `${selectedSuburb}에서 찾기…`
                  : selectedRegion !== "ALL"
                  ? `${australianStates.find((s) => s.id === selectedRegion)?.nameKo || selectedRegion}에서 찾기…`
                  : "검색어를 입력하세요..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-base"
              data-testid="input-search-page"
            />
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            {selectedRegion !== 'ALL' && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span>
                  검색 범위: {selectedSuburb ? `${selectedSuburb}` : australianStates.find((s) => s.id === selectedRegion)?.nameKo || selectedRegion}
                  {selectedRadius !== "all" && ` (${selectedRadius}km)`}
                </span>
              </div>
            )}
            {/* Phase 5.1: Sort presets */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">정렬:</span>
              <div className="flex gap-1">
                <button
                  onClick={() => setSortBy("latest")}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    sortBy === "latest"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted hover:bg-muted/80"
                  }`}
                >
                  최신순
                </button>
                {userLocation && (
                  <button
                    onClick={() => setSortBy("distance")}
                    className={`px-3 py-1 rounded-md text-sm transition-colors ${
                      sortBy === "distance"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted hover:bg-muted/80"
                    }`}
                  >
                    거리순
                  </button>
                )}
                <button
                  onClick={() => setSortBy("popular")}
                  className={`px-3 py-1 rounded-md text-sm transition-colors ${
                    sortBy === "popular"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted hover:bg-muted/80"
                  }`}
                >
                  인기순
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : debouncedQuery ? (
          <div>
            <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <p className="text-lg font-semibold">
                  "{debouncedQuery}" 검색 결과
                </p>
                <Badge variant="secondary">{totalResults}개</Badge>
              </div>
              {/* Phase 5.1: Save search button */}
              {totalResults > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const searchFilters = {
                      radius: selectedRadius,
                      region: selectedRegion,
                      suburb: selectedSuburb,
                      sortBy,
                    };
                    const saved = saveSearch({
                      query: debouncedQuery,
                      filters: searchFilters,
                      notifyOnNew: true,
                    });
                    toast({
                      title: "검색 저장됨",
                      description: "새 글이 올라오면 알림을 받을 수 있습니다.",
                    });
                  }}
                  disabled={isSearchSaved(debouncedQuery, {
                    radius: selectedRadius,
                    region: selectedRegion,
                    suburb: selectedSuburb,
                    sortBy,
                  })}
                >
                  <Bookmark className="h-4 w-4 mr-1" />
                  이 검색 저장
                </Button>
              )}
            </div>

            {totalResults > 0 ? (
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-6">
                  <TabsTrigger value="all" data-testid="tab-all">
                    전체 ({totalResults})
                  </TabsTrigger>
                  {filteredTopics.length > 0 && (
                    <TabsTrigger value="topics" data-testid="tab-topics">
                      <BookOpen className="w-4 h-4 mr-1" />
                      {isKorean ? "문제 해결" : "Life help"} ({filteredTopics.length})
                    </TabsTrigger>
                  )}
                  {filteredCommunityPosts.length > 0 && (
                    <TabsTrigger value="community" data-testid="tab-community">
                      <MessageCircle className="w-4 h-4 mr-1" />
                      {isKorean ? "질문·경험" : "Questions & experiences"} ({filteredCommunityPosts.length})
                    </TabsTrigger>
                  )}
                  {filteredArticles.length > 0 && (
                    <TabsTrigger value="articles" data-testid="tab-articles">
                      <Newspaper className="w-4 h-4 mr-1" />
                      뉴스 ({filteredArticles.length})
                    </TabsTrigger>
                  )}
                  {filteredMarketListings.length > 0 && (
                    <TabsTrigger value="market" data-testid="tab-market">
                      <Package className="w-4 h-4 mr-1" />
                      중고마켓 ({filteredMarketListings.length})
                    </TabsTrigger>
                  )}
                  {filteredMarketplaceItems.length > 0 && (
                    <TabsTrigger value="marketplace" data-testid="tab-marketplace">
                      <ShoppingBag className="w-4 h-4 mr-1" />
                      핫딜 ({filteredMarketplaceItems.length})
                    </TabsTrigger>
                  )}
                  {filteredBusinesses.length > 0 && (
                    <TabsTrigger value="businesses" data-testid="tab-businesses">
                      <Store className="w-4 h-4 mr-1" />
                      업체 ({filteredBusinesses.length})
                    </TabsTrigger>
                  )}
                  {filteredVideos.length > 0 && (
                    <TabsTrigger value="videos" data-testid="tab-videos">
                      <Video className="w-4 h-4 mr-1" />
                      동영상 ({filteredVideos.length})
                    </TabsTrigger>
                  )}
                  {filteredClassifieds.length > 0 && (
                    <TabsTrigger value="classifieds" data-testid="tab-classifieds">
                      <Megaphone className="w-4 h-4 mr-1" />
                      개인광고 ({filteredClassifieds.length})
                    </TabsTrigger>
                  )}
                </TabsList>

                {/* All Results */}
                <TabsContent value="all" className="space-y-8">
                  {filteredTopics.length > 0 && (
                    <div>
                      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <BookOpen className="w-5 h-5" />
                        {isKorean
                          ? `생활문제 해결 (${filteredTopics.length})`
                          : `Life Problem Help (${filteredTopics.length})`}
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredTopics.map((topic) => (
                          <Link key={topic.id} href={`/topics/${topic.id}`}>
                            <Card
                              className="hover-elevate cursor-pointer h-full"
                              data-testid={`card-topic-${topic.id}`}
                            >
                              <CardContent className="p-4">
                                <Badge variant="secondary" className="mb-2">
                                  {isKorean ? "검증된 해결 안내" : "Verified guidance"}
                                </Badge>
                                <h3 className="font-semibold line-clamp-2 mb-1">
                                  {getTopicLabel(topic, locale)}
                                </h3>
                                {getTopicSecondaryLabel(topic, locale) && (
                                  <p className="text-xs text-muted-foreground mb-2">
                                    {getTopicSecondaryLabel(topic, locale)}
                                  </p>
                                )}
                                <p className="text-sm text-muted-foreground line-clamp-3">
                                  {getTopicProblem(topic.id)?.problem[locale] ?? getTopicDescription(topic, locale)}
                                </p>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                      {filteredTopics.length > 3 && (
                        <button
                          onClick={() => setActiveTab("topics")}
                          className="mt-4 text-primary hover:underline"
                        >
                          {isKorean ? "Topic 전체보기 →" : "View all topics →"}
                        </button>
                      )}
                    </div>
                  )}

                  {filteredCommunityPosts.length > 0 && (
                    <div>
                      <h2 className="mb-4 flex items-center gap-2 text-xl font-bold">
                        <MessageCircle className="h-5 w-5" />
                        {isKorean
                          ? `사람들의 질문·경험 (${filteredCommunityPosts.length})`
                          : `Community Questions & Experiences (${filteredCommunityPosts.length})`}
                      </h2>
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {filteredCommunityPosts.slice(0, 6).map((post) => (
                          <Link key={post.id} href={`/community-talk/${post.id}`}>
                            <Card className="h-full cursor-pointer hover-elevate">
                              <CardContent className="p-4">
                                <Badge variant="outline" className="mb-2">
                                  {getCommunityContentIntentLabel(getCommunityContentIntent(post), isKorean ? "ko" : "en")}
                                </Badge>
                                <h3 className="line-clamp-2 font-semibold">{post.title}</h3>
                                {post.content && (
                                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{post.content}</p>
                                )}
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                      {filteredCommunityPosts.length > 6 && (
                        <button onClick={() => setActiveTab("community")} className="mt-4 text-primary hover:underline">
                          {isKorean ? "질문·경험 전체보기 →" : "View all questions & experiences →"}
                        </button>
                      )}
                    </div>
                  )}

                  {filteredArticles.length > 0 && (
                    <div>
                      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Newspaper className="w-5 h-5" />
                        뉴스 ({filteredArticles.length})
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredArticles.slice(0, 6).map((article) => (
                          <Link key={article.id} href={`/article/${article.id}`}>
                            <Card className="hover-elevate cursor-pointer h-full" data-testid={`card-article-${article.id}`}>
                              <CardContent className="p-4">
                                <Badge className="mb-2">{article.category}</Badge>
                                <h3 className="font-semibold line-clamp-2 mb-2">
                                  {article.titleKo || article.title}
                                </h3>
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {article.summaryKo || article.summary}
                                </p>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                      {filteredArticles.length > 6 && (
                        <button
                          onClick={() => setActiveTab("articles")}
                          className="mt-4 text-primary hover:underline"
                        >
                          뉴스 전체보기 →
                        </button>
                      )}
                    </div>
                  )}

                  {filteredMarketListings.length > 0 && (
                    <div>
                      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Package className="w-5 h-5" />
                        중고마켓 ({filteredMarketListings.length})
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {filteredMarketListings.slice(0, 4).map((item) => (
                          <Link key={item.id} href={`/market/${item.id}`}>
                            <Card className="hover-elevate cursor-pointer" data-testid={`card-market-${item.id}`}>
                              {item.images?.[0] && (
                                <img src={item.images[0]} alt={item.title} className="w-full h-40 object-cover rounded-t-lg" />
                              )}
                              <CardContent className="p-3">
                                <h3 className="font-semibold line-clamp-1 mb-1">{item.title}</h3>
                                <p className="text-lg font-bold text-primary">{formatPrice(item.priceCents)}</p>
                                <p className="text-xs text-muted-foreground">{item.suburb || item.state}</p>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                      {filteredMarketListings.length > 4 && (
                        <button
                          onClick={() => setActiveTab("market")}
                          className="mt-4 text-primary hover:underline"
                        >
                          중고마켓 전체보기 →
                        </button>
                      )}
                    </div>
                  )}

                  {filteredMarketplaceItems.length > 0 && (
                    <div>
                      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5" />
                        핫딜 ({filteredMarketplaceItems.length})
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {filteredMarketplaceItems.slice(0, 4).map((item) => (
                          <Link key={item.id} href={`/marketplace/${item.id}`}>
                            <Card className="hover-elevate cursor-pointer" data-testid={`card-marketplace-${item.id}`}>
                              {item.imageUrl && (
                                <img src={item.imageUrl} alt={item.title} className="w-full h-40 object-cover rounded-t-lg" />
                              )}
                              <CardContent className="p-3">
                                <Badge variant="outline" className="mb-1 text-xs">{item.businessName}</Badge>
                                <h3 className="font-semibold line-clamp-1 mb-1">{item.title}</h3>
                                <p className="text-lg font-bold text-primary">${item.salePrice}</p>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                      {filteredMarketplaceItems.length > 4 && (
                        <button
                          onClick={() => setActiveTab("marketplace")}
                          className="mt-4 text-primary hover:underline"
                        >
                          핫딜 전체보기 →
                        </button>
                      )}
                    </div>
                  )}

                  {filteredBusinesses.length > 0 && (
                    <div>
                      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Store className="w-5 h-5" />
                        업체주소록 ({filteredBusinesses.length})
                      </h2>
                      <div className="space-y-3">
                        {filteredBusinesses.slice(0, 5).map((business) => (
                          <Link key={business.id} href={`/business-directory/${business.id}`}>
                            <Card className="hover-elevate cursor-pointer" data-testid={`card-business-${business.id}`}>
                              <CardContent className="p-4">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <h3 className="font-semibold text-lg mb-1">
                                      {business.businessNameKo || business.businessName}
                                    </h3>
                                    <p className="text-sm text-muted-foreground mb-2">
                                      {business.category} · {business.suburb || business.state}
                                    </p>
                                    <p className="text-sm">{business.address}</p>
                                  </div>
                                  {(business.membershipTier === 'premium' || business.membershipTier === 'platinum') && (
                                    <Badge className="bg-blue-600">Premium</Badge>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                      {filteredBusinesses.length > 5 && (
                        <button
                          onClick={() => setActiveTab("businesses")}
                          className="mt-4 text-primary hover:underline"
                        >
                          업체 전체보기 →
                        </button>
                      )}
                    </div>
                  )}

                  {filteredVideos.length > 0 && (
                    <div>
                      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Video className="w-5 h-5" />
                        동영상 ({filteredVideos.length})
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredVideos.slice(0, 3).map((video) => (
                          <Link key={video.id} href={`/youtube/${video.id}`}>
                            <Card className="hover-elevate cursor-pointer" data-testid={`card-video-${video.id}`}>
                              {video.thumbnailUrl && (
                                <img src={video.thumbnailUrl} alt={video.title} className="w-full h-40 object-cover rounded-t-lg" />
                              )}
                              <CardContent className="p-3">
                                <h3 className="font-semibold line-clamp-2">{video.title}</h3>
                                <p className="text-xs text-muted-foreground mt-1">{video.category}</p>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                      {filteredVideos.length > 3 && (
                        <button
                          onClick={() => setActiveTab("videos")}
                          className="mt-4 text-primary hover:underline"
                        >
                          동영상 전체보기 →
                        </button>
                      )}
                    </div>
                  )}

                  {filteredClassifieds.length > 0 && (
                    <div>
                      <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <Megaphone className="w-5 h-5" />
                        개인광고 ({filteredClassifieds.length})
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredClassifieds.slice(0, 4).map((classified) => (
                          <Link key={classified.id} href={`/city-classifieds/${classified.id}`}>
                            <Card className="hover-elevate cursor-pointer" data-testid={`card-classified-${classified.id}`}>
                              <CardContent className="p-4">
                                <Badge variant="outline" className="mb-2">{classified.category}</Badge>
                                <h3 className="font-semibold mb-2">{classified.title}</h3>
                                <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                  {classified.description}
                                </p>
                                <p className="text-xs text-muted-foreground">{classified.suburb || classified.state}</p>
                              </CardContent>
                            </Card>
                          </Link>
                        ))}
                      </div>
                      {filteredClassifieds.length > 4 && (
                        <button
                          onClick={() => setActiveTab("classifieds")}
                          className="mt-4 text-primary hover:underline"
                        >
                          개인광고 전체보기 →
                        </button>
                      )}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="topics">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredTopics.map((topic) => (
                      <Link key={topic.id} href={`/topics/${topic.id}`}>
                        <Card
                          className="hover-elevate cursor-pointer h-full"
                          data-testid={`card-topic-tab-${topic.id}`}
                        >
                          <CardContent className="p-4">
                            <Badge variant="secondary" className="mb-2">
                              {isKorean ? "검증된 해결 안내" : "Verified guidance"}
                            </Badge>
                            <h3 className="font-semibold line-clamp-2 mb-1">
                              {getTopicLabel(topic, locale)}
                            </h3>
                            {getTopicSecondaryLabel(topic, locale) && (
                              <p className="text-xs text-muted-foreground mb-2">
                                {getTopicSecondaryLabel(topic, locale)}
                              </p>
                            )}
                            <p className="text-sm text-muted-foreground line-clamp-3">
                              {getTopicProblem(topic.id)?.problem[locale] ?? getTopicDescription(topic, locale)}
                            </p>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="community">
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredCommunityPosts.map((post) => (
                      <Link key={post.id} href={`/community-talk/${post.id}`}>
                        <Card className="h-full cursor-pointer hover-elevate" data-testid={`card-community-${post.id}`}>
                          <CardContent className="p-4">
                            <Badge variant="outline" className="mb-2">
                              {getCommunityContentIntentLabel(getCommunityContentIntent(post), isKorean ? "ko" : "en")}
                            </Badge>
                            <h3 className="line-clamp-2 font-semibold">{post.title}</h3>
                            {post.content && (
                              <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{post.content}</p>
                            )}
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </TabsContent>

                {/* Individual tabs - showing all results for each category */}
                <TabsContent value="articles">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredArticles.map((article) => (
                      <Link key={article.id} href={`/article/${article.id}`}>
                        <Card className="hover-elevate cursor-pointer h-full" data-testid={`card-article-${article.id}`}>
                          <CardContent className="p-4">
                            <Badge className="mb-2">{article.category}</Badge>
                            <h3 className="font-semibold line-clamp-2 mb-2">
                              {article.titleKo || article.title}
                            </h3>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {article.summaryKo || article.summary}
                            </p>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="market">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {filteredMarketListings.map((item) => (
                      <Link key={item.id} href={`/market/${item.id}`}>
                        <Card className="hover-elevate cursor-pointer" data-testid={`card-market-${item.id}`}>
                          {item.images?.[0] && (
                            <img src={item.images[0]} alt={item.title} className="w-full h-40 object-cover rounded-t-lg" />
                          )}
                          <CardContent className="p-3">
                            <h3 className="font-semibold line-clamp-1 mb-1">{item.title}</h3>
                            <p className="text-lg font-bold text-primary">{formatPrice(item.priceCents)}</p>
                            <p className="text-xs text-muted-foreground">{item.suburb || item.state}</p>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="marketplace">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {filteredMarketplaceItems.map((item) => (
                      <Link key={item.id} href={`/marketplace/${item.id}`}>
                        <Card className="hover-elevate cursor-pointer" data-testid={`card-marketplace-${item.id}`}>
                          {item.imageUrl && (
                            <img src={item.imageUrl} alt={item.title} className="w-full h-40 object-cover rounded-t-lg" />
                          )}
                          <CardContent className="p-3">
                            <Badge variant="outline" className="mb-1 text-xs">{item.businessName}</Badge>
                            <h3 className="font-semibold line-clamp-1 mb-1">{item.title}</h3>
                            <p className="text-lg font-bold text-primary">${item.salePrice}</p>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="businesses">
                  <div className="space-y-3">
                    {filteredBusinesses.map((business) => (
                      <Link key={business.id} href={`/business-directory/${business.id}`}>
                        <Card className="hover-elevate cursor-pointer" data-testid={`card-business-${business.id}`}>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h3 className="font-semibold text-lg mb-1">
                                  {business.businessNameKo || business.businessName}
                                </h3>
                                <p className="text-sm text-muted-foreground mb-2">
                                  {business.category} · {business.suburb || business.state}
                                </p>
                                <p className="text-sm">{business.address}</p>
                              </div>
                              {(business.membershipTier === 'premium' || business.membershipTier === 'platinum') && (
                                <Badge className="bg-blue-600">Premium</Badge>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="videos">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredVideos.map((video) => (
                      <Link key={video.id} href={`/youtube/${video.id}`}>
                        <Card className="hover-elevate cursor-pointer" data-testid={`card-video-${video.id}`}>
                          {video.thumbnailUrl && (
                            <img src={video.thumbnailUrl} alt={video.title} className="w-full h-40 object-cover rounded-t-lg" />
                          )}
                          <CardContent className="p-3">
                            <h3 className="font-semibold line-clamp-2">{video.title}</h3>
                            <p className="text-xs text-muted-foreground mt-1">{video.category}</p>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="classifieds">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredClassifieds.map((classified) => (
                      <Link key={classified.id} href={`/city-classifieds/${classified.id}`}>
                        <Card className="hover-elevate cursor-pointer" data-testid={`card-classified-${classified.id}`}>
                          <CardContent className="p-4">
                            <Badge variant="outline" className="mb-2">{classified.category}</Badge>
                            <h3 className="font-semibold mb-2">{classified.title}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                              {classified.description}
                            </p>
                            <p className="text-xs text-muted-foreground">{classified.suburb || classified.state}</p>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            ) : (
              <div className="text-center py-16">
                <p className="text-lg text-muted-foreground">검색 결과가 없습니다</p>
                <p className="text-sm text-muted-foreground mt-2">다른 검색어로 다시 시도해보세요</p>
                {isKorean && (
                  <div className="mt-6 flex justify-center">
                    <HelpRequestButton sourceType="search" sourceId={debouncedQuery} />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <SearchIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">검색어를 입력해주세요</p>
            <p className="text-sm text-muted-foreground mt-2">뉴스, 중고거래, 핫딜, 업체, 동영상, 개인광고를 검색할 수 있습니다</p>
          </div>
        )}
    </PageContainer>
  );
}
